import { KeyRound, LogOut } from 'lucide-react';
import { useNavigate } from 'react-router';
import { Avatar, AvatarFallback, initialsFrom } from '@/components/ui/avatar';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { useAuth } from '@/features/auth/use-auth';

const ROLE_LABELS: Record<string, string> = {
  ADMIN: 'Administrator',
  HR: 'HR',
  MANAGER: 'Manager',
  EMPLOYEE: 'Employee',
};

export function UserMenu() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  if (!user) return null;

  const handleLogout = async () => {
    await logout();
    // `replace` so Back does not return to a dead authenticated route.
    void navigate('/login', { replace: true });
  };

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="ghost" className="h-9 gap-2 px-2" aria-label="Account menu">
          <Avatar>
            <AvatarFallback>{initialsFrom(user.email)}</AvatarFallback>
          </Avatar>
          <span className="hidden max-w-40 truncate text-sm font-medium sm:inline">
            {user.email}
          </span>
        </Button>
      </DropdownMenuTrigger>

      <DropdownMenuContent align="end" className="w-56">
        <DropdownMenuLabel className="font-normal">
          <p className="truncate text-sm font-medium">{user.email}</p>
          <p className="text-xs text-muted-foreground">{ROLE_LABELS[user.role] ?? user.role}</p>
        </DropdownMenuLabel>

        <DropdownMenuSeparator />

        {/*
          No "My profile" entry. There is no /profile route yet, and the entry
          sent every user to the 404 page. It comes back with a real page built
          on the employee's own record — not a redirect to /employees/:id, which
          is Forbidden for a plain employee.
        */}
        <DropdownMenuItem onSelect={() => void navigate('/change-password')}>
          <KeyRound aria-hidden="true" />
          Change password
        </DropdownMenuItem>

        <DropdownMenuSeparator />

        <DropdownMenuItem destructive onSelect={() => void handleLogout()}>
          <LogOut aria-hidden="true" />
          Sign out
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
